import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  where,
  getCountFromServer,
  serverTimestamp,
  getFirestore,
} from 'firebase/firestore';
import app from '../config/firebaseConfig';

const db = getFirestore(app);
const COLLECTION = 'poojaBookings';

// ─── Fetch bookings with optional filters ─────────────────────────────────
export const fetchPoojaBookings = async ({
  search = '',
  orderStatus = '',
  paymentStatus = '',
} = {}) => {
  try {
    let q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));

    if (orderStatus) {
      q = query(collection(db, COLLECTION), where('orderStatus', '==', orderStatus), orderBy('createdAt', 'desc'));
    } else if (paymentStatus) {
      q = query(collection(db, COLLECTION), where('paymentStatus', '==', paymentStatus), orderBy('createdAt', 'desc'));
    }

    const snapshot = await getDocs(q);
    let bookings = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    if (search) {
      const lower = search.toLowerCase();
      bookings = bookings.filter(b =>
        b.orderId?.toLowerCase().includes(lower) ||
        b.poojaName?.toLowerCase().includes(lower) ||
        b.devoteeDetails?.name?.toLowerCase().includes(lower) ||
        b.devoteeDetails?.phone?.includes(lower) ||
        b.devoteeDetails?.email?.toLowerCase().includes(lower)
      );
    }

    return { success: true, data: bookings, total: bookings.length };
  } catch (error) {
    console.error('fetchPoojaBookings error:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// ─── Get single booking ───────────────────────────────────────────────────
export const getPoojaBookingById = async (bookingDocId) => {
  try {
    const snap = await getDoc(doc(db, COLLECTION, bookingDocId));
    if (!snap.exists()) return { success: false, error: 'Booking not found' };
    return { success: true, data: { id: snap.id, ...snap.data() } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ─── Create booking (user-facing) ─────────────────────────────────────────
export const createPoojaBooking = async (bookingData) => {
  try {
    const orderId = `ORD${Date.now()}`;
    const payload = {
      ...bookingData,
      orderId,
      orderStatus: 'PENDING',
      paymentStatus: 'PENDING',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const docRef = await addDoc(collection(db, COLLECTION), payload);
    return { success: true, id: docRef.id, orderId };
  } catch (error) {
    console.error('createPoojaBooking error:', error);
    return { success: false, error: error.message };
  }
};

// ─── Update order status ──────────────────────────────────────────────────
export const updateOrderStatus = async (bookingDocId, orderStatus) => {
  try {
    await updateDoc(doc(db, COLLECTION, bookingDocId), {
      orderStatus,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ─── Update payment status ────────────────────────────────────────────────
export const updatePaymentStatus = async (bookingDocId, paymentStatus) => {
  try {
    await updateDoc(doc(db, COLLECTION, bookingDocId), {
      paymentStatus,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ─── Delete booking ───────────────────────────────────────────────────────
export const deletePoojaBooking = async (bookingDocId) => {
  try {
    await deleteDoc(doc(db, COLLECTION, bookingDocId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ─── Dashboard stats ──────────────────────────────────────────────────────
export const getBookingStats = async () => {
  try {
    const [totalSnap, confirmedSnap, pendingSnap, paidSnap, cancelledSnap] = await Promise.all([
      getCountFromServer(collection(db, COLLECTION)),
      getCountFromServer(query(collection(db, COLLECTION), where('orderStatus', '==', 'CONFIRMED'))),
      getCountFromServer(query(collection(db, COLLECTION), where('orderStatus', '==', 'PENDING'))),
      getCountFromServer(query(collection(db, COLLECTION), where('paymentStatus', '==', 'PAID'))),
      getCountFromServer(query(collection(db, COLLECTION), where('orderStatus', '==', 'CANCELLED'))),
    ]);

    // Revenue from all bookings
    const allBookings = await getDocs(query(collection(db, COLLECTION), where('paymentStatus', '==', 'PAID')));
    const totalRevenue = allBookings.docs.reduce((sum, d) => sum + (d.data().totalAmount || 0), 0);

    return {
      success: true,
      total: totalSnap.data().count,
      confirmed: confirmedSnap.data().count,
      pending: pendingSnap.data().count,
      paid: paidSnap.data().count,
      cancelled: cancelledSnap.data().count,
      totalRevenue,
    };
  } catch (error) {
    console.error('getBookingStats error:', error);
    return { success: false, total: 0, confirmed: 0, pending: 0, paid: 0, cancelled: 0, totalRevenue: 0 };
  }
};
